export const DRAG_ALIGNMENT_THRESHOLD = 6;

const toFinite = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const normalizeAlignmentRect = (rect = {}) => {
  const left = toFinite(rect.left ?? rect.x);
  const top = toFinite(rect.top ?? rect.y);
  const width = Math.max(0, toFinite(rect.width));
  const height = Math.max(0, toFinite(rect.height));
  const right = Number.isFinite(Number(rect.right))
    ? Number(rect.right)
    : left + width;
  const bottom = Number.isFinite(Number(rect.bottom))
    ? Number(rect.bottom)
    : top + height;

  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    centerX: left + (right - left) / 2,
    centerY: top + (bottom - top) / 2,
  };
};

const horizontalAnchors = (rect) => [rect.left, rect.centerX, rect.right];
const verticalAnchors = (rect) => [rect.top, rect.centerY, rect.bottom];

const findNearestAxisMatch = ({
  sourceAnchors,
  candidates,
  candidateAnchors,
  delta,
  threshold,
}) => {
  let best = null;

  candidates.forEach((candidate) => {
    const targets = candidateAnchors(candidate.rect);
    sourceAnchors.forEach((sourceAnchor, index) => {
      const correction = targets[index] - (sourceAnchor + delta);
      const distance = Math.abs(correction);
      if (distance > threshold) return;
      if (best && distance >= best.distance) return;
      best = {
        candidate,
        correction,
        distance,
        target: targets[index],
      };
    });
  });

  return best;
};

const nearestAnchorDistance = (leftAnchors, rightAnchors) =>
  Math.min(
    ...leftAnchors.flatMap((left) =>
      rightAnchors.map((right) => Math.abs(left - right))
    )
  );

const findEqualSpacingMatch = ({ source, candidates, delta, axis, threshold }) => {
  if (candidates.length < 2) return null;
  const horizontal = axis === "x";
  const sorted = [...candidates].sort((left, right) =>
    horizontal
      ? left.rect.left - right.rect.left
      : left.rect.top - right.rect.top
  );
  const movedStart = horizontal ? source.left + delta : source.top + delta;
  const movedEnd = horizontal ? source.right + delta : source.bottom + delta;
  let best = null;

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const before = sorted[index];
    const after = sorted[index + 1];
    const gap = horizontal
      ? after.rect.left - before.rect.right
      : after.rect.top - before.rect.bottom;
    if (gap < 0) continue;

    const crossAxisDistance = horizontal
      ? nearestAnchorDistance(
          verticalAnchors(before.rect),
          verticalAnchors(after.rect)
        )
      : nearestAnchorDistance(
          horizontalAnchors(before.rect),
          horizontalAnchors(after.rect)
        );
    if (crossAxisDistance > threshold * 2) continue;

    const placements = horizontal
      ? [
          {
            side: "after",
            correction: after.rect.right + gap - movedStart,
          },
          {
            side: "before",
            correction: before.rect.left - gap - movedEnd,
          },
        ]
      : [
          {
            side: "after",
            correction: after.rect.bottom + gap - movedStart,
          },
          {
            side: "before",
            correction: before.rect.top - gap - movedEnd,
          },
        ];

    for (const placement of placements) {
      const distance = Math.abs(placement.correction);
      if (distance > threshold || (best && distance >= best.distance)) continue;
      best = {
        axis,
        before,
        after,
        gap,
        side: placement.side,
        correction: placement.correction,
        distance,
      };
    }
  }

  return best;
};

const buildSpacingGuides = (match, moved) => {
  if (!match) return [];
  if (match.axis === "x") {
    const position = Math.round(
      (match.before.rect.centerY + match.after.rect.centerY + moved.centerY) / 3
    );
    const newStart = match.side === "after" ? match.after.rect.right : moved.right;
    const newEnd = match.side === "after" ? moved.left : match.before.rect.left;
    return [
      {
        kind: "spacing",
        orientation: "horizontal",
        position,
        start: match.before.rect.right,
        end: match.after.rect.left,
      },
      {
        kind: "spacing",
        orientation: "horizontal",
        position,
        start: newStart,
        end: newEnd,
      },
    ];
  }

  const position = Math.round(
    (match.before.rect.centerX + match.after.rect.centerX + moved.centerX) / 3
  );
  const newStart = match.side === "after" ? match.after.rect.bottom : moved.bottom;
  const newEnd = match.side === "after" ? moved.top : match.before.rect.top;
  return [
    {
      kind: "spacing",
      orientation: "vertical",
      position,
      start: match.before.rect.bottom,
      end: match.after.rect.top,
    },
    {
      kind: "spacing",
      orientation: "vertical",
      position,
      start: newStart,
      end: newEnd,
    },
  ];
};

/**
 * Resolve Figma-style edge/center alignment for a dragged rectangle.
 * Geometry is expressed in rendered client pixels; callers can convert the
 * returned delta back to authored/layout pixels when their canvas is scaled.
 */
export const resolveDragAlignment = ({
  sourceRect,
  candidateRects = [],
  deltaX = 0,
  deltaY = 0,
  threshold = DRAG_ALIGNMENT_THRESHOLD,
} = {}) => {
  const source = normalizeAlignmentRect(sourceRect);
  const candidates = candidateRects
    .map((candidate, index) => ({
      id: candidate?.id || `candidate-${index}`,
      rect: normalizeAlignmentRect(candidate?.rect || candidate),
    }))
    .filter(({ rect }) => rect.width > 0 && rect.height > 0);
  const dx = toFinite(deltaX);
  const dy = toFinite(deltaY);
  const limit = Math.max(0, toFinite(threshold, DRAG_ALIGNMENT_THRESHOLD));

  const xMatch = findNearestAxisMatch({
    sourceAnchors: horizontalAnchors(source),
    candidates,
    candidateAnchors: horizontalAnchors,
    delta: dx,
    threshold: limit,
  });
  const yMatch = findNearestAxisMatch({
    sourceAnchors: verticalAnchors(source),
    candidates,
    candidateAnchors: verticalAnchors,
    delta: dy,
    threshold: limit,
  });
  const xSpacingMatch = findEqualSpacingMatch({
    source,
    candidates,
    delta: dx,
    axis: "x",
    threshold: limit,
  });
  const ySpacingMatch = findEqualSpacingMatch({
    source,
    candidates,
    delta: dy,
    axis: "y",
    threshold: limit,
  });
  const useXSpacing =
    Boolean(xSpacingMatch) && (!xMatch || xSpacingMatch.distance < xMatch.distance);
  const useYSpacing =
    Boolean(ySpacingMatch) && (!yMatch || ySpacingMatch.distance < yMatch.distance);

  const resolvedDeltaX =
    dx + (useXSpacing ? xSpacingMatch.correction : xMatch?.correction || 0);
  const resolvedDeltaY =
    dy + (useYSpacing ? ySpacingMatch.correction : yMatch?.correction || 0);
  const moved = normalizeAlignmentRect({
    left: source.left + resolvedDeltaX,
    right: source.right + resolvedDeltaX,
    top: source.top + resolvedDeltaY,
    bottom: source.bottom + resolvedDeltaY,
    width: source.width,
    height: source.height,
  });
  const guides = [];

  if (useXSpacing) {
    guides.push(...buildSpacingGuides(xSpacingMatch, moved));
  } else if (xMatch) {
    guides.push({
      orientation: "vertical",
      position: xMatch.target,
      start: Math.min(moved.top, xMatch.candidate.rect.top),
      end: Math.max(moved.bottom, xMatch.candidate.rect.bottom),
    });
  }
  if (useYSpacing) {
    guides.push(...buildSpacingGuides(ySpacingMatch, moved));
  } else if (yMatch) {
    guides.push({
      orientation: "horizontal",
      position: yMatch.target,
      start: Math.min(moved.left, yMatch.candidate.rect.left),
      end: Math.max(moved.right, yMatch.candidate.rect.right),
    });
  }

  return {
    deltaX: resolvedDeltaX,
    deltaY: resolvedDeltaY,
    snappedX: Boolean(xMatch || xSpacingMatch),
    snappedY: Boolean(yMatch || ySpacingMatch),
    spacedX: useXSpacing,
    spacedY: useYSpacing,
    guides,
  };
};

const findNearestResizeMatch = ({ value, candidates, anchors, threshold }) => {
  let best = null;
  candidates.forEach((candidate) => {
    anchors(candidate.rect).forEach((target) => {
      const correction = target - value;
      const distance = Math.abs(correction);
      if (distance > threshold) return;
      if (best && distance >= best.distance) return;
      best = { candidate, correction, distance, target };
    });
  });
  return best;
};

/**
 * Snap the actively resized edges of a rendered client rect to any edge/center
 * anchor exposed by the other alignable items in the same section.
 */
export const resolveResizeAlignment = ({
  rect,
  candidateRects = [],
  activeEdges = {},
  threshold = DRAG_ALIGNMENT_THRESHOLD,
} = {}) => {
  const source = normalizeAlignmentRect(rect);
  const candidates = candidateRects
    .map((candidate, index) => ({
      id: candidate?.id || `candidate-${index}`,
      rect: normalizeAlignmentRect(candidate?.rect || candidate),
    }))
    .filter(({ rect: candidateRect }) => candidateRect.width > 0 && candidateRect.height > 0);
  const limit = Math.max(0, toFinite(threshold, DRAG_ALIGNMENT_THRESHOLD));
  const horizontalEdge = activeEdges.left ? "left" : activeEdges.right ? "right" : null;
  const verticalEdge = activeEdges.top ? "top" : activeEdges.bottom ? "bottom" : null;
  const xMatch = horizontalEdge
    ? findNearestResizeMatch({
        value: source[horizontalEdge],
        candidates,
        anchors: horizontalAnchors,
        threshold: limit,
      })
    : null;
  const yMatch = verticalEdge
    ? findNearestResizeMatch({
        value: source[verticalEdge],
        candidates,
        anchors: verticalAnchors,
        threshold: limit,
      })
    : null;
  const resolved = { ...source };
  if (xMatch && horizontalEdge) resolved[horizontalEdge] += xMatch.correction;
  if (yMatch && verticalEdge) resolved[verticalEdge] += yMatch.correction;
  resolved.width = Math.max(0, resolved.right - resolved.left);
  resolved.height = Math.max(0, resolved.bottom - resolved.top);
  resolved.centerX = resolved.left + resolved.width / 2;
  resolved.centerY = resolved.top + resolved.height / 2;
  const guides = [];
  if (xMatch) {
    guides.push({
      orientation: "vertical",
      position: xMatch.target,
      start: Math.min(resolved.top, xMatch.candidate.rect.top),
      end: Math.max(resolved.bottom, xMatch.candidate.rect.bottom),
    });
  }
  if (yMatch) {
    guides.push({
      orientation: "horizontal",
      position: yMatch.target,
      start: Math.min(resolved.left, yMatch.candidate.rect.left),
      end: Math.max(resolved.right, yMatch.candidate.rect.right),
    });
  }
  return {
    rect: resolved,
    correctionX: xMatch?.correction || 0,
    correctionY: yMatch?.correction || 0,
    snappedX: Boolean(xMatch),
    snappedY: Boolean(yMatch),
    guides,
  };
};

export const resolveElementLayoutScale = (element) => {
  const parent = element?.offsetParent || element?.parentElement;
  if (!parent?.getBoundingClientRect) return { scaleX: 1, scaleY: 1 };

  const rect = parent.getBoundingClientRect();
  const layoutWidth = Number(parent.clientWidth) || rect.width;
  const layoutHeight = Number(parent.clientHeight) || rect.height;
  const scaleX = rect.width > 0 && layoutWidth > 0 ? rect.width / layoutWidth : 1;
  const scaleY = rect.height > 0 && layoutHeight > 0 ? rect.height / layoutHeight : 1;

  return {
    scaleX: Number.isFinite(scaleX) && scaleX > 0 ? scaleX : 1,
    scaleY: Number.isFinite(scaleY) && scaleY > 0 ? scaleY : 1,
  };
};
